import { Navigate, RouteObject, useLocation } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../store/AuthContext";
import { AccessTypeWithStaff } from "../../Enums/UserEnums";
import { logout, setPrevPathForRedirectAfterLogin } from "../../utils/logout";
import { isJwtExpired } from "../../utils/jwtExpiry";
import { getPersonByUserId } from "../../Services/Person";
import NotFound from "../NotFound";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedPermissions: AccessTypeWithStaff[];
  isCatalogRoute?: boolean;
  isFirmContact?: boolean;
}

const ProtectedRoute = ({
  children,
  allowedPermissions,
  isFirmContact = false,
}: ProtectedRouteProps) => {
  const { tokenDecoded, setIsAuthenticated, loggedPerson } = useContext(AuthContext);
  const [isValidFirmContact, setIsValidFirmContact] = useState<boolean>(false);
  const location = useLocation();

  const userPermission = tokenDecoded?.extension_WSG_Permissions as AccessTypeWithStaff;

  useEffect(() => {
    const validateAccess = async () => {
      if (isFirmContact) {
        setIsValidFirmContact(loggedPerson?.firmAdminAccess || false);
      }
    };

    validateAccess();
  }, [isFirmContact, loggedPerson]);

  useEffect(() => {
    const userId = tokenDecoded?.oid;
    if (!userId || isJwtExpired(tokenDecoded)) {
      return;
    }
    getPersonByUserId(String(userId)).catch(() => undefined);
  }, [location.pathname, tokenDecoded]);

  if (!tokenDecoded || isJwtExpired(tokenDecoded)) {
    const currentPath = location.pathname + location.search;
    setPrevPathForRedirectAfterLogin(currentPath);
    setIsAuthenticated(false);
    logout(false);
    return <Navigate to="/login" replace />;
  }

  if (userPermission === AccessTypeWithStaff.wsgSuper) {
    return <>{children}</>;
  }

  if (isFirmContact && isValidFirmContact) {
    return <>{children}</>;
  }

  if (allowedPermissions.includes(userPermission)) {
    return <>{children}</>;
  }

  return <NotFound />;
};

export const protectRoutes = (
  routes: RouteObject[],
  allowedPermissions: AccessTypeWithStaff[],
  isFirmContact = false
): RouteObject[] => {
  return routes.map((route) => {
    const protectedRoute: RouteObject = {
      ...route,
      element: (
        <ProtectedRoute 
          allowedPermissions={allowedPermissions}
          isFirmContact={isFirmContact}
        >
          {route.element}
        </ProtectedRoute>
      ),
    };

    if (route.children) {
      protectedRoute.children = protectRoutes(
        route.children,
        allowedPermissions,
        isFirmContact
      );
    }

    return protectedRoute;
  });
};
